import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Link2,
  Image,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import {
  createDownloadTask,
  getDownloadTaskStatus,
  submitDownloadCaptcha,
  getPdfDownloadUrl,
} from '../../services/vnEvisaDownloadService';

const VnEvisaDownload = () => {
  // 状态
  const [url, setUrl] = useState('');
  const [taskId, setTaskId] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, creating, fetching_captcha, waiting_captcha, submitting, downloading, success, failed
  const [message, setMessage] = useState('');
  const [captchaImage, setCaptchaImage] = useState(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);

  // 验证 URL 格式
  const isValidUrl = (inputUrl) => {
    try {
      const parsed = new URL(inputUrl);
      return (
        parsed.hostname === 'evisa.gov.vn' &&
        parsed.pathname.includes('/e-visa/search') &&
        parsed.searchParams.get('maHoSo') &&
        parsed.searchParams.get('email')
      );
    } catch {
      return false;
    }
  };

  // 轮询任务状态
  const pollStatus = useCallback(async () => {
    if (!taskId || !polling) return;

    try {
      const data = await getDownloadTaskStatus(taskId);
      setStatus(data.status);
      setMessage(data.message);

      if (data.captcha_image && data.status === 'waiting_captcha') {
        setCaptchaImage(data.captcha_image);
      }

      if (data.error) {
        setError(data.error);
      }

      // 终止状态
      if (['success', 'failed'].includes(data.status)) {
        setPolling(false);
      }
    } catch (err) {
      console.error('轮询状态失败:', err);
    }
  }, [taskId, polling]);

  // 轮询定时器
  useEffect(() => {
    if (!polling) return;

    const interval = setInterval(pollStatus, 2000);
    return () => clearInterval(interval);
  }, [polling, pollStatus]);

  // 创建下载任务
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidUrl(url)) {
      setError('请输入有效的越南官网 E-VISA 搜索链接');
      return;
    }

    setError(null);
    setStatus('creating');
    setMessage('正在创建下载任务...');

    try {
      const data = await createDownloadTask(url);
      setTaskId(data.task_id);
      setStatus(data.status);
      setMessage(data.message);
      setPolling(true);
    } catch (err) {
      setStatus('failed');
      setError(err.message);
      setMessage('创建任务失败');
    }
  };

  // 提交验证码
  const handleCaptchaSubmit = async (e) => {
    e.preventDefault();
    if (!captchaInput.trim()) {
      setError('请输入验证码');
      return;
    }

    setError(null);
    setStatus('submitting');
    setMessage('正在提交验证码...');

    try {
      const data = await submitDownloadCaptcha(taskId, captchaInput.trim());
      setStatus(data.status);
      setMessage(data.message);
      setPolling(true);
    } catch (err) {
      setStatus('failed');
      setError(err.message);
      setMessage('提交验证码失败');
    }
  };

  // 下载 PDF
  const handleDownload = () => {
    const downloadUrl = getPdfDownloadUrl(taskId);
    window.open(downloadUrl, '_blank');
  };

  // 重新开始
  const handleReset = () => {
    setUrl('');
    setTaskId(null);
    setStatus('idle');
    setMessage('');
    setCaptchaImage(null);
    setCaptchaInput('');
    setError(null);
    setPolling(false);
  };

  // 渲染状态图标
  const renderStatusIcon = () => {
    switch (status) {
      case 'creating':
      case 'fetching_captcha':
      case 'submitting':
      case 'downloading':
        return <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />;
      case 'waiting_captcha':
        return <Image className="w-8 h-8 text-amber-500" />;
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'failed':
        return <XCircle className="w-8 h-8 text-red-500" />;
      default:
        return <Link2 className="w-8 h-8 text-emerald-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-lime-50 to-emerald-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center text-emerald-600 hover:text-emerald-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            返回首页
          </Link>
          <h1 className="text-lg font-semibold text-gray-800">E-VISA PDF 下载</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          {/* 标题 */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              {renderStatusIcon()}
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              越南 E-VISA PDF 下载
            </h2>
            <p className="text-gray-600">
              粘贴邮件中的链接，我们帮您下载 E-VISA PDF
            </p>
          </div>

          {/* 消耗积分提示 */}
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-2">📧 如何获取链接</p>
                <ol className="list-decimal list-inside space-y-1 text-amber-700 text-xs md:text-sm">
                  <li>找到越南官网发来的邮件（标题：Notification from Vietnam e-Visa）</li>
                  <li>邮件中有一个蓝色超链接：<strong>"e-Visa Vietnam website"</strong></li>
                  <li>右键点击这个超链接，选择 <strong>"复制链接地址"</strong></li>
                  <li>粘贴到下面的输入框即可</li>
                </ol>
              </div>
            </div>
          </div>

          {/* 邮件示例图片 */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-2">📸 邮件示例（供参考）：</p>
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50 p-2">
              <img
                src="/evisaEmail.png"
                alt="E-VISA邮件示例"
                className="w-full max-h-96 object-contain"
              />
            </div>
          </div>

          {/* 消耗积分提示 */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">⚡ 服务说明</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>本服务消耗 <strong>1 积分</strong></li>
                  <li>下载失败将自动退还积分</li>
                  <li>请不要关闭页面，出现验证码后请你输入</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center text-red-700">
                <XCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* 步骤 1: 输入 URL */}
          {status === 'idle' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  越南官网链接
                </label>
                <textarea
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="请粘贴邮件中的链接，例如：&#10;https://evisa.gov.vn/e-visa/search?email=xxx@gmail.com&maHoSo=E251208..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                  rows={4}
                />
              </div>
              <button
                type="submit"
                disabled={!url.trim()}
                className="w-full py-3 px-6 bg-gradient-to-r from-emerald-500 to-lime-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-lime-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <span className="flex items-center justify-center">
                  <Download className="w-5 h-5 mr-2" />
                  开始下载
                </span>
              </button>
            </form>
          )}

          {/* 步骤 2: 等待验证码 */}
          {['creating', 'fetching_captcha'].includes(status) && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">{message || '正在访问越南官网...'}</p>
              <p className="text-sm text-gray-400 mt-2">请稍候，这可能需要几秒钟</p>
            </div>
          )}

          {/* 步骤 3: 输入验证码 */}
          {status === 'waiting_captcha' && captchaImage && (
            <form onSubmit={handleCaptchaSubmit} className="space-y-6">
              <div className="text-center">
                <p className="text-gray-600 mb-4">请输入下方验证码</p>
                <div className="inline-block p-2 bg-gray-100 rounded-lg mb-4">
                  <img
                    src={`data:image/png;base64,${captchaImage}`}
                    alt="验证码"
                    className="h-12"
                  />
                </div>
              </div>
              <div>
                <input
                  type="text"
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  placeholder="输入验证码"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-2xl tracking-widest focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={!captchaInput.trim()}
                className="w-full py-3 px-6 bg-gradient-to-r from-emerald-500 to-lime-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-lime-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                提交验证码
              </button>
            </form>
          )}

          {/* 步骤 4: 提交中/下载中 */}
          {['submitting', 'downloading'].includes(status) && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">{message || '正在处理...'}</p>
            </div>
          )}

          {/* 步骤 5: 成功 */}
          {status === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                下载成功！
              </h3>
              <p className="text-gray-600 mb-2">{message}</p>

              {/* 数据安全提示 */}
              <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>⚠️ 重要提示：</strong>请立即下载保存，网站不保存您的 E-VISA 数据
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleDownload}
                  className="w-full py-3 px-6 bg-gradient-to-r from-emerald-500 to-lime-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-lime-600 transition-all"
                >
                  <span className="flex items-center justify-center">
                    <FileText className="w-5 h-5 mr-2" />
                    下载 E-VISA PDF
                  </span>
                </button>
                <button
                  onClick={handleReset}
                  className="w-full py-3 px-6 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all"
                >
                  <span className="flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 mr-2" />
                    下载其他 E-VISA
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* 步骤 6: 失败 */}
          {status === 'failed' && (
            <div className="text-center py-8">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                下载失败
              </h3>
              <p className="text-gray-600 mb-6">{error || message}</p>
              <button
                onClick={handleReset}
                className="w-full py-3 px-6 bg-gradient-to-r from-emerald-500 to-lime-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-lime-600 transition-all"
              >
                <span className="flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 mr-2" />
                  重新尝试
                </span>
              </button>
              <p className="text-sm text-gray-500 mt-4">
                积分已自动退还到您的账户
              </p>
            </div>
          )}

          {/* 任务 ID 显示 */}
          {taskId && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                任务 ID: <code className="bg-gray-100 px-2 py-1 rounded">{taskId}</code>
              </p>
            </div>
          )}
        </div>

        {/* 帮助信息 */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>遇到问题？请联系客服获取帮助</p>
        </div>
      </main>
    </div>
  );
};

export default VnEvisaDownload;
